// Copyright ©️ 2026 eVolve MEP, LLC

import { Fragment, useMemo } from 'react';

import type { MachineElement } from '@evolvemep/api-ts';
import { Divider, Flex, Group, Stack, Switch, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { WrappedColorPicker } from 'components/Mantine/ColorPickers/WrappedColorPicker';
import { ToggleButton } from 'components/Mantine/ToggleButton';
import { isNotNilOrEmptyString } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import type { useBendModelBrowserState } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendModelBrowserState';

const elementSettingsToggles = [
  {
    label: 'Fit to view',
    setting: 'fitToView',
  },
  {
    label: 'Ghost hidden objects',
    setting: 'ghosting',
  },
] as const;

export const BendModelBrowserControls = ({
  state: previewState,
  toggle,
  setColor,
  isolate,
  element,
}: { element: MachineElement } & ReturnType<typeof useBendModelBrowserState>) => {
  const { spool, sourceIds } = element;
  // At least one of these should be present in order for the model browser to be displayed.
  // So we shouldn't need to worry about a render condition for this list being empty.
  const controlSections = useMemo(
    () =>
      [
        { type: 'spool' as const, data: spool, label: `Spool ${spool}` },
        { type: 'element' as const, data: sourceIds, label: element.name },
      ].filter(({ data }) => isNotNilOrEmptyString(data)),
    [spool, element.name, sourceIds],
  );
  return (
    <Stack gap={0} w={300}>
      {controlSections.map(({ type, label }) => (
        <Fragment key={type}>
          <Flex p="xs" justify="space-between">
            <Group gap="xs" flex={1} miw={0}>
              <EvolveIcon icon={type === 'spool' ? 'Spools' : 'Fabrication'} />
              <Text truncate flex={1}>
                {label}
              </Text>
            </Group>
            <Group gap={4} flex="0 0 auto">
              <ToggleButton
                checked={previewState[`${type}ColorEnabled` as const]}
                onToggle={() => toggle(`${type}ColorEnabled` as const)}
                actionLabelWhenChecked={`Disable color key for ${label}`}
                actionLabelWhenUnchecked={`Enable color key for ${label}`}
                icon="ToggleShading"
              />
              <WrappedColorPicker
                label={`${label} color`}
                value={previewState[`${type}Color` as const]}
                onChange={(c) => setColor(`${type}Color` as const, c)}
                transparent={!previewState[`${type}ColorEnabled` as const]}
              />
              <ToggleButton
                checked={previewState.isolating === type}
                onToggle={() => isolate(previewState.isolating === type ? 'model' : type)}
                actionLabelWhenChecked="Return to full model view"
                actionLabelWhenUnchecked={`Isolate ${label}`}
                icon="Isolate"
              />
            </Group>
          </Flex>
          <Divider />
        </Fragment>
      ))}
      {elementSettingsToggles.map(({ label, setting }, i, arr) => (
        <Fragment key={label}>
          <Flex p="xs">
            <Switch
              label={label}
              labelPosition="left"
              checked={previewState[setting]}
              onChange={() => toggle(setting)}
              styles={{
                root: {
                  flex: 1,
                  display: 'flex',
                },
                body: {
                  flex: 1,
                  justifyContent: 'space-between',
                },
              }}
            />
          </Flex>
          {i !== arr.length - 1 && <Divider />}
        </Fragment>
      ))}
    </Stack>
  );
};
